/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { LAST_ACTIVE_CAMPAIGN_KEY } from '../pages/ChatSocketPage/utils/campaignRestore'
import { useNavigate } from 'react-router-dom'
import { SubscriptionService, UserAccountsService } from '../services'
import { useLocalStorage } from './useLocalStorage'
import { getCurrentSubscriptionPlanName } from '../utils/subscription'
import { clearSessionInvalidation, hasValidAuthSession, isPublicAuthRoute } from '../utils/unauthorizedSession'

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useLocalStorage('userData', null)
  const [token, setToken] = useLocalStorage('token', null)
  const [clientData, setClientData] = useLocalStorage('clientData', null)
  const [quickRegistration, setQuickRegistration] = useState(false)
  const [sirstLoginAux, setFirstLoginAux] = useState(false)
  const [userPlan, setUserPlan] = useState(null)

  const navigate = useNavigate()

  const preservePremiumParam = (path) => {
    const isPremiumInStorage = localStorage.getItem('premium') === 'true'
    if (isPremiumInStorage && path) {
      const separator = path.includes('?') ? '&' : '?'
      return `${path}${separator}premium=true`
    }
    return path
  }

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(LAST_ACTIVE_CAMPAIGN_KEY)
    } catch {
      // noop
    }
    setUserData(null)
    setToken(null)
    setClientData(null)
    setUserPlan(null)
  }, [setClientData, setToken, setUserData])

  const login = useCallback(async (data, routerNavigate, state) => {
    clearSessionInvalidation()

    if (data?.data) {
      setUserData(data.data)
      setToken(data.data.token)
    } else {
      setUserData(data)
      setToken(data.token)
    }

    if (routerNavigate) {
      const finalPath = preservePremiumParam(routerNavigate)
      navigate(finalPath, { state })
      return
    }

    const selectedPlan = await UserAccountsService.getSelectedPlan()
    let hasBranding = false

    try {
      const { getBusinessByLogin } = await import('../services/business')
      const { getBrandingByBusinessId } = await import('../services/brandingService')
      const businessResp = await getBusinessByLogin()
      const businessId = businessResp?.data?.[0]?.uuid

      if (businessId) {
        const brandingResp = await getBrandingByBusinessId(businessId)
        hasBranding = Array.isArray(brandingResp?.data) && brandingResp.data.length > 0
      }
    } catch {
      hasBranding = false
    }

    if (!selectedPlan.data.selectedPlan && !hasBranding) {
      const planCheckoutPath = preservePremiumParam('/PlanCheckout')
      navigate(planCheckoutPath)
    }

    const activeSubscription = await SubscriptionService.getActiveSubscription()
    const activeSubscriptions = activeSubscription?.data ? [activeSubscription.data] : []
    setUserPlan(getCurrentSubscriptionPlanName(activeSubscriptions))

    navigate('/ChatSocket')
  }, [navigate, setToken, setUserData])

  const logout = useCallback(() => {
    clearSessionInvalidation()
    clearSession()
    navigate('/SignIn', { replace: true })
  }, [clearSession, navigate])

  useEffect(() => {
    if (userData && !hasValidAuthSession()) {
      clearSession()
    }
  }, [clearSession, userData])

  useEffect(() => {
    const handleSessionExpired = () => {
      clearSession()

      if (!isPublicAuthRoute(window.location.pathname)) {
        navigate('/SignIn', { replace: true })
      }
    }

    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired)
    }
  }, [clearSession, navigate])

  const value = useMemo(
    () => ({
      userData,
      login,
      logout,
      quickRegistration,
      setQuickRegistration,
      setUserData,
      userPlan,
      setUserPlan,
    }),
    [login, logout, quickRegistration, userData, userPlan],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  return useContext(AuthContext)
}
